import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faCircleInfo, faFloppyDisk, faMemoPad } from "@fortawesome/pro-solid-svg-icons";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuProps } from "antd/es/menu";
import Tooltip from "antd/es/tooltip";
import Paragraph from "antd/es/typography/Paragraph";
import cn from "classnames";
import { useIntl } from "react-intl";

import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { Button, Dropdown, Popconfirm } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import { getEmailTemplates, isEmailTemplateUpdating } from "@app/store/email";
import { fetchEmailTemplates, updateEmailTemplate } from "@app/store/email/actions";
import { getCurrentUserId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import EmailTemplateInfo from "@app/types/email/EmailTemplateInfo";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useEmailTemplateInfoGetter from "@app/util/email/useEmailTemplateInfoGetter";
import useEntityTypeEmailTemplates from "@app/util/email/useEntityTypeEmailTemplates";

import TemplatesModal from "../../EmailTemplatesModal";
import EmailContext from "../EmailContext";

import styles from "./EmailTemplateButton.module.scss";
import messages from "./messages";
import EmailTemplateCreateModal from "./TemplateCreateModal";

interface EmailTemplateButtonProps {
  className?: string;
  reply?: boolean;
}

const EmailTemplateButton = ({
  className,
  currentUserId,
  onTemplatesFetch,
  onTemplateUpdate,
  reply = false,
  templateUpdating,
}: EmailTemplateButtonProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const {
    attachedFiles,
    body,
    emailFilled,
    entities,
    entityType,
    onPreviewRecipientIdChange,
    onTemplateChange,
    previewRecipientId,
    resources,
    subject,
    template,
    templateChanged,
  } = useNonNullableContext(EmailContext);

  const [templateCreateModalVisible, showNewTemplateModal, hideNewTemplateModal] = useBoolean();
  const [templatesModalVisible, showTemplatesModal, hideTemplatesModal] = useBoolean();
  const [templatesModalInitialSelectedTemplateId, setTemplatesModalInitialSelectedTemplateId] =
    useState<EmailTemplate["id"] | undefined>();

  const templates = useEntityTypeEmailTemplates(entityType);
  const templateInfoGetter = useEmailTemplateInfoGetter();

  useEffect(() => {
    onTemplatesFetch();
  }, [onTemplatesFetch]);

  const handleTemplateClick = useDynamicCallback((template: EmailTemplate) => {
    if (!templateInfoGetter(template.id)!.valid) {
      setTemplatesModalInitialSelectedTemplateId(template.id);
      showTemplatesModal();
    } else {
      onTemplateChange(template);
    }
  });

  const handleMoreTemplatesClick = useDynamicCallback(() => {
    setTemplatesModalInitialSelectedTemplateId(template?.id);
    showTemplatesModal();
  });

  const handleTemplateApply = useDynamicCallback((templateInfo: EmailTemplateInfo) => {
    hideTemplatesModal();
    onTemplateChange(templateInfo.template);
  });

  const handleTemplateClear = useDynamicCallback(() => {
    onTemplateChange(undefined);
  });

  const handleNewTemplateModalHide = useDynamicCallback((template?: EmailTemplate) => {
    hideNewTemplateModal();
    if (template) {
      onTemplateChange(template);
      analytics.clicked(["Save New Template"], { name: template.name });
    }
  });

  const handleUpdate = useDynamicCallback(() => {
    if (template) {
      onTemplateUpdate({
        attachments: attachedFiles.map(({ file }) => file).filter(isDefined),
        callback: (template) => {
          onTemplateChange(template);
        },
        resources: Array.from(resources.values()),
        template: {
          ...template,
          body: body,
          subject: subject,
        },
      });
    }
  });

  const menuItems: MenuProps["items"] = useMemo(() => {
    const getMenuItem = (text: string, icon?: React.ReactNode) => (
      <span className={styles.menuItem}>
        <span className={styles.menuItemIcon}>{icon}</span>
        <Paragraph
          className={styles.menuItemText}
          ellipsis={{
            expandable: false,
            rows: 1,
            tooltip: {
              placement: "right",
            },
          }}
        >
          {text}
        </Paragraph>
      </span>
    );

    return [
      ...(templates.length > 0
        ? [
            {
              className: styles.headerMenuItem,
              key: "header",
              label: (
                <div className={styles.headerMenuItemContainer}>
                  <span className={styles.mostRecentLabel}>
                    {intl.formatMessage(messages.mostRecent)}
                  </span>
                  <Popconfirm
                    cancelText={intl.formatMessage(messages.deleteConfirmationCancel)}
                    description={intl.formatMessage(messages.clearConfirmationText)}
                    disabled={!templateChanged}
                    okText={intl.formatMessage(messages.deleteConfirmationOk)}
                    onConfirm={handleTemplateClear}
                    placement="right"
                    zIndex={1051} // higher than the dropdown menu
                  >
                    <ButtonLink
                      disabled={!template}
                      onClick={templateChanged ? stopEvents : handleTemplateClear}
                    >
                      {intl.formatMessage(messages.clear)}
                    </ButtonLink>
                  </Popconfirm>
                </div>
              ),
            },
            ...templates.map((mapTemplate) => ({
              className: mapTemplate.id === template?.id ? styles.selected : undefined,
              key: `template-${mapTemplate.id}`,
              label: getMenuItem(
                mapTemplate.name,
                mapTemplate.id === template?.id ? <FontAwesomeIcon icon={faCheck} /> : undefined
              ),
              onClick: () => handleTemplateClick(mapTemplate),
            })),
            ...(templates.length
              ? [
                  {
                    key: "seeMoreDivider",
                    type: "divider",
                  },
                  {
                    key: "seeMore",
                    label: (
                      <div className={styles.seeMoreContainer}>
                        <span>{getMenuItem(intl.formatMessage(messages.seeMore))}</span>
                        <FontAwesomeIcon icon={faChevronRight} />
                      </div>
                    ),
                    onClick: handleMoreTemplatesClick,
                  },
                ]
              : []),
            {
              key: "divider",
              type: "divider",
            },
          ]
        : []),
      {
        disabled: !emailFilled,
        key: "saveNew",
        label: (
          <Tooltip
            placement="right"
            title={
              emailFilled
                ? undefined
                : reply
                  ? intl.formatMessage(messages.saveNewDisabledReply)
                  : intl.formatMessage(messages.saveNewDisabled)
            }
          >
            {getMenuItem(
              intl.formatMessage(messages.saveNew),
              <FontAwesomeIcon icon={faFloppyDisk} />
            )}
          </Tooltip>
        ),
        onClick: emailFilled ? showNewTemplateModal : undefined,
      },
    ];
  }, [
    emailFilled,
    handleMoreTemplatesClick,
    handleTemplateClear,
    handleTemplateClick,
    intl,
    reply,
    showNewTemplateModal,
    template,
    templateChanged,
    templates,
  ]);

  const isCurrentUserSelectedTemplateOwner = template?.userId === currentUserId;
  const isSelectedTemplateShared = template?.userIds.length;

  return (
    <div className={cn(className, styles.container)}>
      <Dropdown menu={{ items: menuItems }} overlayClassName={styles.dropdown} trigger={["click"]}>
        <div className={styles.buttonContainer}>
          <div
            className={cn(styles.button, {
              [styles.buttonWithTemplate]: !!template,
              [styles.buttonWithTemplateHasChanges]: templateChanged,
            })}
          >
            {templateChanged ? (
              <FontAwesomeIcon className={styles.templateHasChangedIcon} icon={faCircleInfo} />
            ) : (
              <FontAwesomeIcon icon={faMemoPad} />
            )}
            <span className={styles.buttonText}>
              {template?.name ?? intl.formatMessage(messages.selectTemplate)}
            </span>
            <FontAwesomeIcon icon={faChevronDown} />
          </div>
        </div>
      </Dropdown>
      {templateChanged && isCurrentUserSelectedTemplateOwner && (
        <Tooltip title={intl.formatMessage(messages.updateDescription)}>
          <Popconfirm
            cancelText={intl.formatMessage(messages.updateConfirmationCancel)}
            description={intl.formatMessage(
              isSelectedTemplateShared
                ? messages.updateSharedConfirmationText
                : messages.updateConfirmationText
            )}
            okText={intl.formatMessage(messages.updateConfirmationOk)}
            onConfirm={handleUpdate}
            overlayClassName={styles.updateConfirmation}
            placement="bottom"
          >
            <span>
              <Button
                data-action-id="update-template"
                disabled={templateUpdating}
                loading={templateUpdating}
                type="link"
              >
                {intl.formatMessage(messages.update)}
              </Button>
            </span>
          </Popconfirm>
        </Tooltip>
      )}
      {templateChanged && !isCurrentUserSelectedTemplateOwner && (
        <Tooltip title={intl.formatMessage(messages.saveNewSharedDescription)}>
          <span>
            <Button onClick={showNewTemplateModal} type="link">
              {intl.formatMessage(messages.saveNew)}
            </Button>
          </span>
        </Tooltip>
      )}
      {templateCreateModalVisible && (
        <EmailTemplateCreateModal onHide={handleNewTemplateModalHide} />
      )}
      {templatesModalVisible && (
        <TemplatesModal
          applyLabel={intl.formatMessage(messages.applyTemplate)}
          initialSelectedTemplateId={templatesModalInitialSelectedTemplateId}
          onApply={handleTemplateApply}
          onHide={hideTemplatesModal}
          onSelectedRecipientIdChange={onPreviewRecipientIdChange}
          recipients={entities}
          recipientType={entityType}
          selectedRecipientId={previewRecipientId}
          showPreviewAs
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  templates: getEmailTemplates(state),
  templateUpdating: isEmailTemplateUpdating(state),
});

const mapDispatchToProps = {
  onTemplatesFetch: fetchEmailTemplates.request,
  onTemplateUpdate: updateEmailTemplate.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailTemplateButton);
